import { useEffect, useMemo } from 'react'

import confetti from 'canvas-confetti'
import clsx from 'clsx'
import _ from 'lodash'
import { useSession } from '@/shared/services/session/compat'

import { useBottomSheetContext } from '@/shared/contexts/BottomSheetContext'
import { useUserProgress } from '@/shared/hooks/useUserProgress'
import { MobileSheet } from '@/shared/ui/MobileSheet'

import { PrizeSheetDetails } from './PrizeSheetDetails'
import { DigitalProductPrizeParticipationResult } from './Result'
import { DigitalProductPrizeParticipationRules } from './Rules'
import { getPrizeParticipationHint } from '../lib/prizeParticipationHint'
import {
  getPrizeEligibleParticipantsLabel,
  getPrizeIneligibilityHint
} from '../lib/prizeRuleMessage'
import {
  getPrizeUiStatus,
  isPrizeWinner,
  resolvePrizeWinners
} from '../lib/prizeUiStatus'

const fireConfetti = () => {
  confetti({
    particleCount: 300,
    spread: 400,
    startVelocity: 40,
    scalar: 1.2,
    ticks: 200,
    origin: { y: 0.5 },
    zIndex: 9999
  })
}

export const DigitalProductPrizeBottomSheet = ({
  id,
  media,
  name,
  onHide,
  executed,
  prize_rules,
  // O Parceiro do concurso: sob a regra de check-in e ele que define quem
  // concorre, e e o nome que o Participante precisa ler na regra.
  partner,
  winners,
  open = true
}) => {
  const { hideBottomSheet } = useBottomSheetContext()
  const { participatingPrizes } = useUserProgress()
  const { data: session } = useSession()

  const isParticipating = participatingPrizes?.includes(id)

  /* Por identificador e sobre a coleção, igual a `usePrizesListView`.
   *
   * `PrizeSerializer` não emite mais `winner_progress` e
   * `Prize::WinnerSerializer` não emite e-mail desde a redução de PII
   * (design.md §4.5 e §7.1). Comparar e-mail aqui deixava `isWinner` sempre
   * falso: sem confete, sem "(você)", sem o estado `success` que vira o rótulo
   * "Ganhei" — justamente para quem chegou clicando no aviso de vitória.
   *
   * `resolvePrizeWinners` devolve o próprio array quando ele já é um: não há
   * alocação por render, e por isso nenhum dos dois precisa de memo. */
  const announcedWinners = resolvePrizeWinners({ winners })
  const isWinner = isPrizeWinner({ winners }, session?.user?.id)
  const hasWinner = announcedWinners.length > 0

  const uiStatus = useMemo(
    () =>
      getPrizeUiStatus(
        { executed, prize_rules },
        { isParticipating, isWinner }
      ),
    [executed, prize_rules, isParticipating, isWinner]
  )

  const participationHint = useMemo(() => {
    const ineligibilityHint =
      uiStatus === 'locked' ? getPrizeIneligibilityHint(prize_rules, partner) : null

    return getPrizeParticipationHint(uiStatus, ineligibilityHint)
  }, [uiStatus, prize_rules, partner])

  const eligibleParticipantsLabel = getPrizeEligibleParticipantsLabel(prize_rules)
  const imageUrl = _.get(media, 'file.url')
  const isMuted = uiStatus === 'locked' || uiStatus === 'expired'

  useEffect(() => {
    if (!isWinner) return undefined

    const timer = setTimeout(() => fireConfetti(), 1000)
    return () => clearTimeout(timer)
  }, [isWinner])

  const handleDismiss = () => {
    hideBottomSheet()
    onHide?.()
  }

  if (!open) return null

  return (
    <MobileSheet open={open} onClose={handleDismiss} title={name || 'Concurso'}>
      {imageUrl ? (
        <div className="-mx-4 mb-4 max-h-[220px] overflow-hidden bg-gray-200">
          <img
            src={imageUrl}
            alt=""
            className={clsx(
              'block h-full max-h-[220px] w-full object-cover',
              isMuted && 'opacity-65 grayscale'
            )}
          />
        </div>
      ) : null}

      <PrizeSheetDetails
        uiStatus={uiStatus}
        participationHint={participationHint}
        eligibleParticipantsLabel={eligibleParticipantsLabel}
      />

      <DigitalProductPrizeParticipationRules
        prizeRules={prize_rules}
        isParticipating={isParticipating}
        prizePartner={partner}
      />

      {hasWinner && (
        <DigitalProductPrizeParticipationResult
          winners={announcedWinners}
          currentUserId={session?.user?.id}
        />
      )}
    </MobileSheet>
  )
}
